/**
 * Act 1 — T1 onboarding (Al Madar's verification flow). Account
 * creation → entity registration → AML → live verification → real-time
 * catalogue → proposal popup → OrbID fee → OrbID materialisation +
 * handshake #2.
 *
 * Materialisation (1.8) is a small stateless component inlined here
 * since it's only used by this act — it was previously inline in
 * SceneRouter alongside ContractCaptureWithHandshake.
 */

import type { ReactElement } from "react";
import type { Act1Screen } from "@/lib/scenes/screens";
import type { SceneRenderContext } from "@/lib/scenes/act4";
import type { EventLogState } from "@/lib/sse/useSSE";
import type { GraphState } from "@/components/web/types";
import { AccountCreation }       from "@/components/scenes/act1/AccountCreation";
import { EntityRegistration }    from "@/components/scenes/act1/EntityRegistration";
import { AmlScreen }             from "@/components/scenes/act1/AmlScreen";
import { LiveVerificationCall }  from "@/components/scenes/act1/LiveVerificationCall";
import { RealTimeCatalogue }     from "@/components/scenes/act1/RealTimeCatalogue";
import { OrbIdFeeCheckout }      from "@/components/scenes/act1/OrbIdFeeCheckout";
import { OrbIdMaterialisation }  from "@/components/scenes/act1/OrbIdMaterialisation";
import { SplitScreen }           from "@/components/scene/SplitScreen";

// pickLvcPhase + pickPaymentState live in SceneRouter for now — they're
// tiny pure helpers used only here. If they grow, move into helpers.ts.
function pickLvcPhase(ev: EventLogState): "intro" | "id" | "challenge" | "signature" | "complete" {
  const evs = ev.events;
  if (evs.some((e) => e.eventType === "verification.LiveSessionCompleted"))    return "complete";
  if (evs.some((e) => e.eventType === "verification.SignatureCaptured"))       return "signature";
  if (evs.some((e) => e.eventType === "verification.ChallengePhraseSpoken"))   return "challenge";
  if (evs.some((e) => e.eventType === "verification.IdShown"))                 return "id";
  return "intro";
}

function pickPaymentState(ev: EventLogState): "ready" | "authorising" | "approved" {
  if (ev.events.some((e) => e.eventType === "payment.Reconciled"))   return "approved";
  if (ev.events.some((e) => e.eventType === "payment.Dispatched"))   return "authorising";
  return "ready";
}

function Materialisation({ ev, graph }: { ev: EventLogState; graph: GraphState }) {
  const events = ev.events;
  const proposalSent = events.some((e) => e.eventType === "trade.ProposalSubmitted");
  const proposalAccepted = events.some((e) => e.eventType === "trade.ProposalAccepted");
  if (proposalAccepted) {
    return (
      <SplitScreen
        number={2}
        caption="Aurora's catalogue dashboard receives the proposal · Al Madar's status: Proposal accepted · trade enters Trace."
        left={
          <div className="mt-6 grid place-items-center text-[12px] text-orbitra-paper">
            <p className="text-state-verified">Proposal received from Al Madar Foods</p>
            <p className="mt-2 text-orbitra-muted">28 t Halal whole chicken · USD 850K · CIF Jebel Ali</p>
            <p className="mt-2 text-policy-pass">Accepted ✓ · contract ORB-CTR-2026-0042</p>
          </div>
        }
        right={
          <div className="mt-6 grid place-items-center text-[12px] text-orbitra-paper">
            <p className="text-state-verified">Awaiting acceptance…</p>
            <p className="mt-2 text-policy-pass">Accepted by Aurora ✓</p>
          </div>
        }
      />
    );
  }
  return <OrbIdMaterialisation phase={proposalSent ? "submitted" : "proposal"} graph={graph} />;
}

export const ACT1_RENDERERS: Record<Act1Screen, (ctx: SceneRenderContext) => ReactElement> = {
  "1.1": () => <AccountCreation />,
  "1.2": () => <EntityRegistration />,
  "1.3": () => <AmlScreen />,
  "1.4": ({ ev }) => <LiveVerificationCall phase={pickLvcPhase(ev)} />,
  "1.5": () => <RealTimeCatalogue showProposalPopup={false} />,
  "1.6": () => <RealTimeCatalogue showProposalPopup />,
  "1.7": ({ ev }) => <OrbIdFeeCheckout authState={pickPaymentState(ev)} />,
  "1.8": ({ ev, graph }) => <Materialisation ev={ev} graph={graph} />,
};
